import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, STATUS_FLOW, STATUS_COLORS, PAY_COLORS } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Copy, FileText, BellRing, Smartphone, Pencil, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { buildReadyMessage, openWhatsApp, openSms } from "@/lib/notify";
import { useConfirm } from "@/components/ConfirmProvider";

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings, isAdmin } = useAuth();
  const { confirm } = useConfirm();
  const cur = settings?.currency || "KMF";
  const [o, setO] = useState(null);
  const [methods, setMethods] = useState([]);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [notify, setNotify] = useState(false);
  const [kgVal, setKgVal] = useState("");

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => { setO(r.data); setKgVal(String(r.data.total_kg ?? 0)); }).catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
    api.get("/payment-methods", { params: { active_only: true } }).then((r) => { setMethods(r.data); if (r.data[0]) setPayMethod(r.data[0].name); });
  }, [load]);

  if (!o) return <p className="text-slate-400">Chargement...</p>;

  const readyMsg = () => buildReadyMessage(o, settings);
  const notifyWhatsApp = () => { openWhatsApp(o.customer_phone, readyMsg()); setNotify(false); };
  const notifySms = () => { openSms(o.customer_phone, readyMsg()); setNotify(false); };

  const setStatus = async (status) => {
    try {
      const { data } = await api.put(`/orders/${id}/status`, { status });
      setO(data);
      toast.success(`Statut: ${status}`);
      if (status === "Prêt") setNotify(true);
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const recordPayment = async () => {
    const amt = Number(payAmt);
    if (!amt || amt <= 0) return toast.error("Montant invalide");
    try { const { data } = await api.post(`/orders/${id}/payment`, { amount: amt, method: payMethod }); setO(data); setPayAmt(""); toast.success("Paiement enregistré"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveKg = async () => {
    try {
      const { data } = await api.put(`/orders/${id}/kg`, { total_kg: Number(kgVal) || 0 });
      setO(data); setKgVal(String(data.total_kg ?? 0));
      toast.success("Poids mis à jour — réattribuez la carte pour recalculer la fidélité");
    } catch (e) { toast.error(apiError(e)); }
  };

  const settleOrder = async () => {
    if (!(await confirm({ title: "Solder le reste", description: `Solder le reste de ${fmtMoney(o.remaining, cur)} en arrondi ? La commande passera en « Payé » (ce montant ne sera pas ajouté à la caisse).`, confirmText: "Solder" }))) return;
    try {
      const { data } = await api.post(`/orders/${id}/settle`);
      setO(data);
      toast.success("Reste soldé (arrondi)");
    } catch (e) { toast.error(apiError(e)); }
  };

  const receiptText = () => {
    const s = settings || {};
    const lines = [
      `*${s.company_name || "WuraPro"}*`,
      s.address ? s.address : null,
      s.whatsapp ? `WhatsApp: ${s.whatsapp}` : null,
      "------------------------------",
      `Reçu N°: ${o.order_number}`,
      `Date: ${fmtDate(o.created_at)}`,
      `Client: ${o.customer_name} (${o.customer_phone})`,
      `Agent: ${o.agent_name}`,
      "------------------------------",
      ...o.items.map((it) => `${it.service_name} x ${it.quantity}${it.pricing_type === "per_kg" ? "kg" : ""} = ${fmtMoney(it.line_total, cur)}`),
      "------------------------------",
      `Total: ${fmtMoney(o.total, cur)}`,
      `Payé: ${fmtMoney(o.paid, cur)}`,
      `Reste: ${fmtMoney(o.remaining, cur)}`,
      `Statut: ${o.status} | ${o.payment_status}`,
      s.receipt_footer ? `\n${s.receipt_footer}` : null,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const sendWhatsApp = () => {
    const phone = (o.customer_phone || "").replace(/\D/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(receiptText())}`;
    window.open(url, "_blank");
  };

  const copyReceipt = async () => {
    try { await navigator.clipboard.writeText(receiptText()); toast.success("Reçu copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const selCls = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-5">
          <Card className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h1 className="text-2xl font-bold text-slate-800" data-testid="order-detail-number">{o.order_number}</h1>
                <p className="text-slate-500">{o.customer_name} · {o.customer_phone}</p>
                <p className="text-xs text-slate-400">Agent: {o.agent_name} · {fmtDate(o.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                {o.status !== "Livré" && (o.paid || 0) === 0 && (
                  <Button size="sm" variant="outline" data-testid="edit-order-btn" onClick={() => nav(`/orders/${o.id}/edit`)}><Pencil size={14} className="mr-1" /> Modifier</Button>
                )}
                <span className={`text-sm px-3 py-1 rounded-full ${STATUS_COLORS[o.status]}`} data-testid="order-detail-status">{o.status}</span>
                <span className={`text-sm px-3 py-1 rounded-full ${PAY_COLORS[o.payment_status]}`}>{o.payment_status}</span>
              </div>
            </div>

            <table className="data-table w-full text-sm mt-4">
              <thead className="text-slate-400 text-left"><tr><th className="py-2">Service</th><th>Type</th><th>Qté</th><th className="text-right">Sous-total</th></tr></thead>
              <tbody>
                {o.items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2">{it.service_name}</td>
                    <td className="text-slate-500">{it.pricing_type === "per_kg" ? `${fmtMoney(it.unit_price, cur)}/kg` : "Forfait"}</td>
                    <td>{it.quantity}{it.pricing_type === "per_kg" ? " kg" : ""}</td>
                    <td className="text-right">{fmtMoney(it.line_total, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-sm">
              {o.discount_amount > 0 && (
                <>
                  <div className="flex justify-between"><span className="text-slate-500">Sous-total</span><span className="font-medium text-slate-700">{fmtMoney(o.subtotal, cur)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Remise {o.discount_percent ? `(-${o.discount_percent}%)` : ""}{o.campaign_name ? ` · ${o.campaign_name}` : ""}</span><span className="text-rose-600 font-medium">-{fmtMoney(o.discount_amount, cur)}</span></div>
                </>
              )}
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold" data-testid="detail-total">{fmtMoney(o.total, cur)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Payé</span><span className="text-emerald-600 font-medium" data-testid="detail-paid">{fmtMoney(o.paid, cur)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Reste à payer</span><span className="text-rose-600 font-medium" data-testid="detail-remaining">{fmtMoney(o.remaining, cur)}</span></div>
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-sm">
              <span className="text-slate-500" title="Poids réel du linge, compté dans la carte fidélité au cumul de kilos">Poids total (fidélité)</span>
              {isAdmin ? (
                <div className="flex items-center gap-2">
                  <Input data-testid="order-kg-input" type="number" min="0" step="0.1" className="w-24 h-8" value={kgVal} onChange={(e) => setKgVal(e.target.value)} />
                  <span className="text-slate-400 text-xs">kg</span>
                  <Button size="sm" variant="outline" data-testid="save-kg-btn" onClick={saveKg}>Enregistrer</Button>
                </div>
              ) : <span className="font-medium" data-testid="detail-kg">{o.total_kg || 0} kg</span>}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Faire avancer le statut</h3>
            <div className="flex flex-wrap gap-2">
              {STATUS_FLOW.map((s) => (
                <Button key={s} size="sm" variant={o.status === s ? "default" : "outline"} data-testid={`status-${s}`} onClick={() => setStatus(s)} className={o.status === s ? "bg-sky-600" : ""}>{s}</Button>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Enregistrer un paiement</h3>
            {o.remaining > 0 ? (
              <div className="space-y-3">
                <div><Label>Montant</Label><Input data-testid="payment-amount" type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder={`Max ${o.remaining}`} /></div>
                <div><Label>Moyen</Label>
                  <select data-testid="payment-method" className={`${selCls} w-full`} value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                    {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <Button data-testid="record-payment-btn" onClick={recordPayment} className="w-full bg-emerald-600 hover:bg-emerald-700">Encaisser</Button>
                <button data-testid="settle-order-btn" onClick={settleOrder} className="w-full text-xs text-slate-500 hover:text-sky-600 underline">Solder le reste en arrondi ({fmtMoney(o.remaining, cur)})</button>
              </div>
            ) : <p className="text-emerald-600 text-sm">Commande entièrement payée ✓{o.rounding_adjustment > 0 && <span className="text-slate-400"> (arrondi {fmtMoney(o.rounding_adjustment, cur)})</span>}</p>}
            {o.payments?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 text-xs space-y-1">
                {o.payments.map((p, i) => (
                  <div key={i} className="flex justify-between text-slate-500"><span>{p.method} · {fmtDate(p.date)}</span><span>{fmtMoney(p.amount, cur)}</span></div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2"><BellRing size={18} className="text-amber-500" /> Notifier le client</h3>
            <p className="text-xs text-slate-500 mb-3">Prévenir le client que sa commande est prête (message court, sans facture).</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" data-testid="notify-whatsapp" onClick={notifyWhatsApp} className="text-emerald-600 border-emerald-200"><MessageCircle size={16} className="mr-1" /> WhatsApp</Button>
              <Button variant="outline" size="sm" data-testid="notify-sms" onClick={notifySms}><Smartphone size={16} className="mr-1" /> SMS</Button>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Reçu</h3>
            <Button data-testid="open-receipt-btn" onClick={() => nav(`/orders/${id}/receipt`)} className="w-full bg-slate-900 hover:bg-slate-800 mb-2">
              <FileText size={16} className="mr-2" /> Voir & partager le reçu
            </Button>
            <Button data-testid="print-eticket-btn" onClick={() => nav(`/orders/${id}/receipt?print=1`)} className="w-full bg-emerald-600 hover:bg-emerald-700 mb-2">
              <Printer size={16} className="mr-2" /> Imprimer l'e-ticket
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" data-testid="receipt-whatsapp" onClick={sendWhatsApp}><MessageCircle size={16} className="mr-1" /> WhatsApp</Button>
              <Button variant="outline" size="sm" data-testid="receipt-copy" onClick={copyReceipt}><Copy size={16} className="mr-1" /> Copier</Button>
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={notify} onOpenChange={setNotify}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing size={20} className="text-amber-500" /> Commande prête !</DialogTitle>
            <DialogDescription>Voulez-vous prévenir {o.customer_name} ({o.customer_phone}) que sa commande est prête ?</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button data-testid="notify-dialog-whatsapp" onClick={notifyWhatsApp} className="bg-emerald-600 hover:bg-emerald-700"><MessageCircle size={16} className="mr-2" /> Envoyer via WhatsApp</Button>
            <Button data-testid="notify-dialog-sms" onClick={notifySms} variant="outline"><Smartphone size={16} className="mr-2" /> Envoyer par SMS</Button>
            <Button variant="ghost" onClick={() => setNotify(false)} className="text-slate-500">Plus tard</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
